/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * Reusable Violation Modal - Displays Guard Rails content policy violations
 * with consistent UI across chat, resume upload, and knowledge base upload.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';

const VIOLATION_ICONS = {
  pii: '🔐',
  profanity: '🚫',
  political: '🏛️',
  harmful: '⚠️',
  hate_speech: '🚷',
  injection: '🛡️',
  spam: '📢',
  off_topic: '🎯'
};

const VIOLATION_COLORS = {
  pii: 'bg-red-50 border-red-300',
  profanity: 'bg-orange-50 border-orange-300',
  political: 'bg-purple-50 border-purple-300',
  harmful: 'bg-red-50 border-red-300',
  hate_speech: 'bg-red-50 border-red-300',
  injection: 'bg-yellow-50 border-yellow-300',
  spam: 'bg-blue-50 border-blue-300',
  off_topic: 'bg-gray-50 border-gray-300'
};

const SOURCE_LABELS = {
  profile_content: 'Resume Upload',
  document_upload: 'Knowledge Base Upload',
  chat_message: 'Chat Message'
};

export default function ViolationModal({ show, onClose, violationData }) {
  const navigate = useNavigate();

  if (!show || !violationData) return null;

  const { violation, strikes, warning, action, support } = violationData;
  const violationType = violation?.type || 'default';
  const icon = VIOLATION_ICONS[violationType] || '🚫';
  const colorClass = VIOLATION_COLORS[violationType] || 'bg-red-50 border-red-300';
  const sourceLabel = SOURCE_LABELS[violation?.source] || 'Upload';

  // If suspended, redirect
  const isSuspended = strikes?.remaining === 0 || warning?.level === 'SUSPENDED' || violationData.forceLogout;

  const handleClose = () => {
    if (isSuspended) {
      localStorage.removeItem('token');
      localStorage.setItem('suspensionInfo', JSON.stringify(violationData));
      navigate('/suspended');
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full rounded-xl shadow-2xl border-2 ${colorClass} overflow-hidden`}>

        {/* Header */}
        <div className="bg-white bg-opacity-80 px-6 pt-6 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{icon}</span>
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  {violation?.title || 'Content Policy Violation'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Detected in: {sourceLabel}</p>
              </div>
            </div>
            {!isSuspended && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 space-y-4">

          {/* Description */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-gray-700 text-sm">{violation?.description}</p>
            {violation?.userMessage && (
              <p className="text-gray-600 mt-2 text-sm">
                <strong>What to do:</strong> {violation.userMessage}
              </p>
            )}
          </div>

          {/* Strike Counter */}
          {strikes && (
            <div className="bg-white rounded-lg p-4 shadow-sm text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <ShieldAlert className="w-5 h-5 text-red-500" />
                <span className="text-xl font-bold text-red-600">
                  Strike {strikes.total} of {strikes.maxAllowed}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {strikes.remaining > 0
                  ? `${strikes.remaining} remaining before account suspension`
                  : 'Account suspended'}
              </p>
            </div>
          )}

          {/* Warning Message */}
          {warning && (
            <div className={`p-3 rounded-lg text-sm font-medium ${
              warning.level === 'FINAL_WARNING'
                ? 'bg-red-100 text-red-800 border border-red-300'
                : warning.level === 'SUSPENDED'
                  ? 'bg-red-200 text-red-900 border border-red-400'
                  : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
            }`}>
              {warning.message}
            </div>
          )}

          {/* Support */}
          <div className="text-center text-sm text-gray-500">
            Questions? Contact{' '}
            <a
              href={`mailto:${support?.email || 'support@skilldelivery.co.in'}`}
              className="text-blue-600 underline"
            >
              {support?.email || 'support@skilldelivery.co.in'}
            </a>
          </div>

          {/* Action Button */}
          <button
            onClick={handleClose}
            className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
              isSuspended
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            {isSuspended ? 'Go to Suspension Page' : 'I Understand'}
          </button>
        </div>
      </div>
    </div>
  );
}
